import React, { lazy, Suspense, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import Header from "../components/Header"
import Footer from "../components/Footer"
import ProjectNavigation from "../components/ProjectNavigation"

// Lazy load the ResponsiveImage component (used if/when images are added)
const ResponsiveImage = lazy(() => import("../components/ResponsiveImage"))

// Collapsible section component
const CollapsibleSection = ({ title, isOpen, onToggle, children }) => (
  <div style={{ marginBottom: "0" }}>
    <button
      onClick={onToggle}
      style={{
        width: "100%",
        padding: "16px 0",
        fontSize: "18px",
        fontWeight: "500",
        fontFamily: "Fanwood, serif",
        backgroundColor: "transparent",
        color: "#000",
        border: "none",
        borderTop: "1px solid #000",
        borderRadius: "0",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        transition: "opacity 0.2s ease"
      }}
      onMouseEnter={(e) => e.target.style.opacity = "0.6"}
      onMouseLeave={(e) => e.target.style.opacity = "1"}
    >
      <span>{title}</span>
      <span style={{
        transition: "transform 0.3s ease",
        fontSize: "18px",
        lineHeight: 1
      }}>
        {isOpen ? "−" : "+"}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          style={{ paddingTop: "30px", overflow: "hidden" }}
        >
          {children}
          <div style={{ 
            display: "flex", 
            justifyContent: "center", 
            marginTop: "40px",
            marginBottom: "20px"
          }}>
            <button
              onClick={onToggle}
              style={{
                padding: "10px 24px",
                fontSize: "14px",
                fontWeight: "500",
                backgroundColor: "transparent",
                color: "#000",
                border: "1px solid #000",
                borderRadius: "0",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#000"
                e.target.style.color = "#fff"
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent"
                e.target.style.color = "#000"
              }}
            >
              Close {title}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
)

const SagaAdaptiveApparel = () => {
  // State for collapsible sections
  const [ideationOpen, setIdeationOpen] = useState(false)
  const [researchOpen, setResearchOpen] = useState(false)
  const [flatsOpen, setFlatsOpen] = useState(false)
  const [rendersOpen, setRendersOpen] = useState(false)
  const [realImagesOpen, setRealImagesOpen] = useState(false)
  const [finalResultsOpen, setFinalResultsOpen] = useState(false)

  const openSection = ideationOpen
    ? { label: "Ideation & Identity", close: () => setIdeationOpen(false) }
    : researchOpen
    ? { label: "Research", close: () => setResearchOpen(false) }
    : flatsOpen
    ? { label: "Flats", close: () => setFlatsOpen(false) }
    : rendersOpen
    ? { label: "Renders", close: () => setRendersOpen(false) }
    : realImagesOpen
    ? { label: "Real Images", close: () => setRealImagesOpen(false) }
    : finalResultsOpen
    ? { label: "Final Results", close: () => setFinalResultsOpen(false) }
    : null
  // Add image base paths (no extension) here when assets are ready.
  const images = [
    // "/images/projects/SAGA Adaptive Apparel/Look 01",
  ]

  const radarChartImage = "/images/projects/SAGA Adaptive Apparel/Radar_Analysis"
  const inspirationBoardImage = "/images/projects/SAGA Adaptive Apparel/Inspiration Board"
  const sagaLogo = "/images/projects/SAGA Adaptive Apparel/SAGA_Logo"
  const logoIdeationBoard = "/images/projects/SAGA Adaptive Apparel/Logo_Ideation_Board"

  const LoadingPlaceholder = () => (
    <div style={{
      width: "100%",
      height: "400px",
      backgroundColor: "#f5f5f5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <div style={{
        width: "40px",
        height: "40px",
        border: "3px solid #f3f3f3",
        borderTop: "3px solid #000",
        borderRadius: "50%",
        animation: "spin 1s linear infinite"
      }} />
    </div>
  )

  // Competitor brand data
  const nonAdaptiveBrands = [
    {
      name: "ACRONYM",
      description: "A technical apparel brand founded by Errolson Hugh. Acronym creates \"the world's most structurally extreme\" casual wear, focusing on function and technical innovation. Its aesthetic is dark, cyberpunk-inspired and utilitarian.",
      categories: "High-tech outerwear (multilayer Gore-Tex and windproof jackets), pants, vests, tops and accessories (bags, footwear) designed for urban outdoor performance.",
      pricePoint: "Designer/luxury segment",
      priceRange: "Jackets and technical pieces retail around $1,300-$2,100 USD and pants about $800-$1200. Sale prices range roughly $838-$1,900 for garments.",
      targetMarket: "Style conscious individuals who value high functionality and avant-garde design. Sold globally through select high-end retailers.",
      ageRange: "Young adults approx. 20-35",
      location: "Founded in Munich, Germany; high popularity in Asia",
      targetedConsumers: "Physically active urbanites; values mobility and performance",
      competitiveEdge: "Renowned for cutting-edge techwear features: modular systems, integrated carrying straps, \"gravity pockets\", quick-release zippers, etc. Celebrated for extreme weatherproofing and seamless garment integration through manufacturing.",
      aesthetics: "Dark, minimalist and tactical (often described as dystopian or cyberpunk). Clothing looks technical and futuristic.",
      materials: "High-end technical textiles (Gore-Tex Pro/3L fabrics, Schoeller Dryskin™, Cordura nylon, etc.). Durable ripstop and waterproof membranes dominate the line.",
      fit: "Tailored for ergonomic function: articulated sleeves, tapered pants, adjustable hems. Generally slim but engineered for layering and movement.",
      designFeatures: "Signature innovations include internal jacket-sling systems (to carry jackets hands-free), gravity-feed pockets, and multi-use pockets; hidden hoods and modular layers that interlock across garments.",
      innovations: "Focus on construction and material technology. First-person design philosophy, patterning and pocketing done from the first-person point of view for pure ultimate function."
    },
    {
      name: "Greg Lauren",
      description: "A luxury menswear/womenswear label founded by designer Greg Lauren. Known for a deconstructive, collage-like aesthetic, tearing fabrics apart to put back together in a new form using vintage and surplus materials. It channels Americana.",
      categories: "Tailored suiting, shirts, denim, outerwear, knitwear and accessories (hats, scarves, etc.), often mixing elegant pieces with rugged details.",
      pricePoint: "Designer/luxury segment",
      priceRange: "Ready-to-wear tops and pants run in the low hundreds ($300-800), while unique jackets or coats can reach several thousand dollars (e.g., $5,000+)",
      targetMarket: "Fashion-forward individuals who appreciate experimental design. The brand markets to both men and women as high-fashion, avant-garde streetwear.",
      ageRange: "Adults approx. 20-45",
      location: "Founded in Los Angeles, USA",
      targetedConsumers: "Appeals to those who like artisanal, one-of-a-kind pieces.",
      competitiveEdge: "Its reconstructed approach and storytelling heritage (Greg Lauren is related to Ralph Lauren) set it apart. Each garment often looks handcrafted or repurposed, giving an imperfectly perfect vibe.",
      aesthetics: "Highly deconstructed and artsy, clothing appears layered, patched, frayed or shredded. The palette is earthy and vintage (military greens, worn denims, neutral tones) reflecting the use of aged fabrics.",
      materials: "A mix of cottons, denims, wool knits and genuine leather, often aged or upcycled (military surplus twill, vintage suit cloth, patched denim).",
      fit: "Generally relaxed or oversized silhouettes with purposeful irregularities (uneven hems, mixed paneling). Many items drape loosely due to patchwork construction.",
      designFeatures: "Visible raw edges, hand-stitched seams, patchwork panels and layered details (e.g., one-piece shirts/jackets made of many scrap pieces).",
      innovations: "Innovation is conceptual rather than technological, its signature is the visual innovation of reconstruction. Greg Lauren's uniqueness lies in craftsmanship and upcycling."
    },
    {
      name: "Engineered Garments",
      description: "A bridge primarily menswear label Founded by Daiki Suzuki. It \"masterfully blends classic Americana with a Japanese aesthetic,\" producing meticulously detailed workwear-inspired clothes.",
      categories: "Sharp-casual and utilitarian pieces: chore jackets, overshirts, military-style vests, trousers (chinos, cargo pants), rugged denim jackets, and accessories (bags, caps).",
      pricePoint: "Better/bridge segment",
      priceRange: "A signature jacket is around $400-700, shirts ~$150-300. The highest pieces (typically coats) stay under $800-900.",
      targetMarket: "Individuals who appreciate high-quality, heritage-inspired sportswear and workwear. Strong following among menswear enthusiasts interested in utility aesthetics.",
      ageRange: "Adults approx. 25-50",
      location: "Founded in New York City, USA. Large Tokyo, Japan division",
      targetedConsumers: "General active consumers; drawn to functional detailing and relaxed tailored style.",
      competitiveEdge: "Unparalleled craftsmanship and abundance of detail. Engineered Garments prides itself on \"engineering\" garments, e.g., hidden gussets, reinforced stitching, extra pockets, and performance fabrics. Every piece has subtle refinements for function and longevity.",
      aesthetics: "A mix of vintage Americana (safari jackets, hunter vests, collegiate shirts) with Japanese restraint. Colors are often muted (olive, tan, denim indigo, khaki).",
      materials: "Sturdy, high-quality materials: natural cottons (canvas, twills, chambray), heavyweight denim, wool gabardine, occasional nylon-coated fabrics. Reflects heritage utility (lots of 10-14 oz fabrics).",
      fit: "Cuts are typically classic while slightly boxy but can vary from partially to extremely relaxed. Designed for layering, with longer lengths on tops and higher backs on jackets.",
      designFeatures: "Emphasis on pockets, double-layer collars, reinforced elbows and cuffs, adjustable tabs at waists. Functional details dominate.",
      innovations: "Focused on construction rather than tech. The engineered aspect is innovation in detailing and sewing techniques. Collaborations also bring hybrid products, but core innovations are in meticulous patterning and rugged construction."
    }
  ]

  const adaptiveBrands = [
    {
      name: "IZ Adaptive",
      description: "An adaptive fashion label founded in 2009 by Izzy Camilleri. IZ was \"born from a mission to address clothing challenges faced by individuals with physical disabilities,\" pioneering stylish, functional designs for wheelchair and limited-mobility users.",
      categories: "Primarily lower-body garments (pants, jeans, shorts with adaptive waistbands and no back seat seam), but also shirts, jackets, dresses and undergarment accessories. The line includes modern basics (tees, joggers) and tailored pieces (e.g., seated suit jackets).",
      pricePoint: "Moderate segment",
      priceRange: "Daily basics sell ~$50-$120 (with frequent sales), while specialty items reach ~$400-500. Overall, prices stay in the range of ~$50 to $500 per garment.",
      targetMarket: "Wheelchair users and people with physical disabilities of all ages. Aimed at anyone wanting adaptive solutions without looking medical.",
      ageRange: "Primarily adults approx. 20-60",
      location: "Founded in Toronto, Canada",
      targetedConsumers: "Consumers with limited lower-body mobility (paraplegia, mobility impairment, post-surgery recovery, etc.), including wheelchair users. Emphasizes inclusivity and confidence.",
      competitiveEdge: "One of the first and most-awarded adaptive brands. IZ Adaptive emphasizes patent-protected innovations (like the Seamless Back Pant to reduce pressure sores) and has gained media and institutional recognition. Its designs are fashionable and gender-neutral rather than clinical.",
      aesthetics: "Clean, contemporary styles that closely resemble regular casual and dresswear. Neutral and professional color palette (denim indigo, blacks, grays, navy, camo, classic plaids). The look appears unisex and understated.",
      materials: "Soft, performance fabrics and stretch denim engineered for seated wear. Fabrics prioritize comfort and ease of movement (stretch denim, soft knits).",
      fit: "Adapted for sitting posture: higher back rises on pants, elastic or drawstring waists that sit at mid-back, relaxed fit through hips. Shirts/jackets may have longer backs or cutaway backs. Many items feature zip-off sections.",
      designFeatures: "Magnetic closures (no zipper or with magnetic fly) for easier fastening, hidden adjustable hems, built-in shower-friendly underwear pockets.",
      innovations: "The Seated Seamless Back Pant (patented) is a hallmark innovation to prevent pressure sores."
    },
    {
      name: "Unhidden Clothing",
      description: "An adaptive fashion brand founded in 2016 by Victoria Jenkins. Unhidden is award-winning and explicitly inclusive, designing for \"people with and without visible, as well as non-visible disabilities.\" It was the first adaptive label in the British Fashion Council and stages its own London Fashion Week shows.",
      categories: "Everyday and occasion wear: dresses, tops, skirts, trousers, jumpsuits, and outerwear. Also seated categories specially cut for wheelchair users. Collaborations include lines with mass-market brands (e.g., Lucy and Yak).",
      pricePoint: "Budget segment",
      priceRange: "Basic tops and dresses are around about $50-$65. More complex garments (e.g., specialized coats or tailored suits) may be higher.",
      targetMarket: "Broad: adults with disabilities (including wheelchair users, amputees, people with chronic illnesses, and even short-stature individuals) and allies.",
      ageRange: "Adults and seniors approx. 20-80",
      location: "Founded in London, United Kingdom",
      targetedConsumers: "People with diverse capabilities, from mobility impairments to invisible conditions (fatigue, arthritis, sensory issues). Also anyone seeking inclusive design. Unhidden specifically mentions accommodating those undergoing hospital treatment, etc.",
      competitiveEdge: "Unhidden pushes adaptive wear into mainstream fashion, emphasizing that disabled people represent a large market yet have been underserved. It uses \"strategic fastenings, detachable seams and non-restrictive fabrications\" to blend style with function.",
      aesthetics: "Modern, often feminine. Recent collections mixed rich jewel tones and 1970s-inspired silhouettes (three-piece suits, wide-leg jumpsuits) while keeping clothing elegant. The designs look like typical women's wear (dresses, high-neck tops, culottes) but with subtle accessibility features.",
      materials: "Mostly lightweight, stretchy, natural or semi-natural fabrics (cottons, rayons, lyocell blends). Designed for comfort (breathable, non-irritating).",
      fit: "Adaptive: e.g., no back (for seated comfort), elastic panels, adjustable waists, and extra back length. Pants have higher back rise for wheelchair sitting, tops have back cut-outs or slits for ease of dressing. Seated garments are cut extra-long in back.",
      designFeatures: "Hidden adaptive details: magnetic buttons or velcro under normal-looking buttons, quiet leg openings (no abrasive seams), drop crotches, easy-open necklines, and even featuring elements like glittery pockets for medical devices.",
      innovations: "Unhidden's innovation lies in universal design integration, e.g., making trousers and dresses that anyone could wear, but which solve common disability problems (e.g., hidden front-fastening bras, hospital-friendly zips). Its founder's adaptive expertise and first-of-kind fashion shows are also notable innovations for awareness."
    },
    {
      name: "Kinetic Balance",
      description: "An adaptive clothing label specializing in \"chair-riding apparel\", clothing specifically tailored for wheelchair users. It is a world leader in this niche.",
      categories: "Premium casual and outdoor wear: stretch denim \"jeans made for sitting,\" jackets (3-in-1 fall/spring coats, wet-weather jackets), pullovers, rain gear (e.g., Raindek leg covers), hats and bags all designed for seated use.",
      pricePoint: "Contemporary/Better segment",
      priceRange: "Denim are about $126, jackets $290-$380, technical rain wear ~$175-$243. So, roughly $100-$350 per garment.",
      targetMarket: "Wheelchair users worldwide who want both style and function.",
      ageRange: "Adults and seniors approx. 20-80",
      location: "Founded in Katwijk, Netherlands",
      targetedConsumers: "People with full-time seated mobility (spinal injuries, neuro conditions). Their products are explicitly for wheelchair riders (quadriplegics to paraplegics).",
      competitiveEdge: "Deep expertise in wheelchair-specific design. They call their clothes wheelchair clothing and emphasize that adaptive is not the right term, it's simply clothing for people who sit. The founder developed prototypes with feedback from users (notably an active Quadriplegic ambassador team). Kinetic Balance's edge is a complete product ecosystem built on fifteen years of \"Positioning Products\" experience.",
      aesthetics: "Modern and understated. Clothing resembles standard styles but is cut differently. Colors are solid and understated. The look is functional yet clean, avoiding any medical appearance.",
      materials: "High-stretch, durable fabrics. E.g., all jeans use stretch denim. Rain garments use technical waterproof stretch fabrics. Fabrics are chosen to sit well (reduce friction/pressure) and feel like regular clothes.",
      fit: "Cut from a seated perspective: higher waistband (back) than front (50/50 fit for quadriplegics); tapered legs, no rear pockets. Jackets have more length in back and often no back vent, so they stay covered while seated. Most pants come in regular or slim.",
      designFeatures: "Special high-seat shaping to prevent riding down. Many tops/jackets have magnet closures and grab handles. They also make unique items like Raindek leg covers and magnetic compression belts. Their Quad/Tetraplegic jacket has expandable front and fixed back to accommodate sitting bellies.",
      innovations: "They continuously iterate with user feedback positioning the brand as highly customer-driven."
    }
  ]

  return (
    <div style={{ overflowX: "hidden" }}>
      <Header />
      <ProjectNavigation currentProject="/saga-adaptive-apparel" />

      {/* Floating close button — visible whenever a section is expanded */}
      <style>{`
        .saga-float-close {
          position: fixed;
          bottom: 32px;
          right: 32px;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          background-color: #000;
          color: #fff;
          border: none;
          border-radius: 0;
          font-size: 14px;
          font-family: Fanwood, serif;
          letter-spacing: 0.04em;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(0,0,0,0.25);
          transition: opacity 0.2s ease;
          white-space: nowrap;
        }
        .saga-float-close:hover { opacity: 0.7; }
        /* Tablet */
        @media (max-width: 1023px) {
          .saga-float-close {
            bottom: 24px;
            right: 24px;
            font-size: 13px;
            padding: 11px 18px;
          }
        }
        /* Mobile — stretch full width along the bottom */
        @media (max-width: 767px) {
          .saga-float-close {
            bottom: 0;
            right: 0;
            left: 0;
            width: 100%;
            justify-content: center;
            padding: 16px 20px;
            font-size: 15px;
            box-shadow: 0 -2px 16px rgba(0,0,0,0.18);
            gap: 12px;
          }
        }
      `}</style>
      {openSection && (
        <button
          className="saga-float-close"
          onClick={openSection.close}
        >
          <span style={{ fontSize: "16px", lineHeight: 1 }}>−</span>
          Close {openSection.label}
        </button>
      )}

      <div style={{
        textAlign: "center",
        padding: "30px 20px 20px",
        marginBottom: "10px"
      }}>
        <h1 style={{
          fontSize: "32px",
          margin: 0,
          fontWeight: "bold"
        }}>SAGA Adaptive Apparel</h1>

        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "40px",
          maxWidth: "1000px",
          margin: "30px auto 0",
          padding: "0 20px",
          flexWrap: "wrap"
        }}>
          <div style={{
            flex: "0 0 200px",
            maxWidth: "200px"
          }}>
            <Suspense fallback={<LoadingPlaceholder />}>
              <ResponsiveImage
                src={`${sagaLogo}.jpg`}
                alt="SAGA Logo"
                style={{ width: "100%", height: "auto", objectFit: "contain" }}
                sizes="200px"
                priority={true}
              />
            </Suspense>
          </div>
          <p style={{
            flex: "1 1 400px",
            maxWidth: "440px",
            fontSize: "26px",
            lineHeight: 1.6,
            color: "#333",
            textAlign: "left",
            margin: 0
          }}>
            Capstone project exploring adaptive apparel design through SAGA, a user-centered brand focused on accessibility, modularity, and inclusive stylish, high fashion apparel.
          </p>
        </div>
      </div>

      <main>
        <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
          
          {/* Ideation & Identity Collapsible Section */}
          <CollapsibleSection 
            title="Ideation & Identity" 
            isOpen={ideationOpen} 
            onToggle={() => setIdeationOpen(!ideationOpen)}
          >
          {/* Design Statement Section */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "20px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Design Statement
            </h2>
            <p style={{ 
              maxWidth: "900px",
              margin: "0 auto",
              fontSize: "18px",
              lineHeight: 1.8,
              color: "#333",
              padding: "20px 30px",
              backgroundColor: "#f9f9f9",
              borderRadius: "8px",
              borderLeft: "4px solid #333",
              textAlign: "left"
            }}>
              This project reimagines adaptive clothing for young adults in wheelchairs, creating pieces that are experimental, fashion-forward, and expressive. By fusing thoughtful functional design with striking silhouettes and innovative fabrics, it brings modern, high-fashion and avant-garde sensibilities to a space that has long lacked style.
            </p>
          </section>

          {/* Inspiration Section */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "30px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Inspiration
            </h2>

            <div style={{ 
              display: "flex", 
              justifyContent: "center", 
              marginBottom: "30px",
              maxWidth: "1000px",
              margin: "0 auto 30px"
            }}>
              <Suspense fallback={<LoadingPlaceholder />}>
                <ResponsiveImage
                  src={`${inspirationBoardImage}.jpg`}
                  alt="SAGA Adaptive Apparel Inspiration Board"
                  style={{ width: "100%", height: "auto", objectFit: "contain" }}
                  sizes="(max-width: 1000px) 100vw, 1000px"
                  priority={false}
                />
              </Suspense>
            </div>

            <div style={{ 
              maxWidth: "900px",
              margin: "0 auto",
              fontSize: "16px",
              lineHeight: 1.8,
              color: "#333",
              textAlign: "left"
            }}>
              <p style={{ marginBottom: "20px" }}>
                My work draws deep inspiration from the technical sophistication and engineered precision of assistive technologies and mobility devices, particularly wheelchairs. Rather than perceiving these instruments as symbols of limitation, the project positions them as aesthetic and conceptual catalysts for design. This perspective acknowledges the agency these technologies confer, enhancing both functional capacity and the user's engagement with their surroundings.
              </p>
              
              <p style={{ marginBottom: "20px" }}>
                The visual and material language of machinery and urban infrastructure further informed the design direction, merging with the reconstructed sensibility found in the work of designers such as Greg Lauren. I hope to conceive my garments as modular and adaptive systems that respond fluidly to changing contexts and physical demands. This adaptability reflects influences from military design philosophy, as seen in Engineered Garments and ACRONYM, where utilitarian detailing and versatility support a coherent and deliberate visual identity. I was particularly drawn to systemic modes of construction found in saddlery, satellite arrays, and suspension systems, where discrete components interconnect to form unified structures that enhance storage potential and accommodate medical or personal equipment without compromising aesthetic integrity.
              </p>
              
              <p style={{ margin: 0 }}>
                Functionally, designing apparel for seated users requires a precise understanding of form, ergonomics, and construction. My process centered on the architecture of seams, exploring their removal, repositioning, or strategic articulation to minimize pressure and improve sensory comfort. These technical decisions were intentionally revealed rather than concealed, allowing the garment's structural logic to emerge as a defining visual feature. The result is a synthesis of form and function in which technical precision and aesthetic expression operate in thoughtful equilibrium.
              </p>
            </div>

            {/* Inspiration Board Sources */}
            <div style={{ 
              maxWidth: "900px",
              margin: "30px auto 0",
              fontSize: "14px",
              lineHeight: 1.8,
              color: "#666"
            }}>
              <p style={{ marginBottom: "10px", fontWeight: "bold" }}>Sources:</p>
              <ol style={{ margin: 0, paddingLeft: "20px" }}>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://telfar.net/collections/fleece-denim/products/saggy-sweat-jean-blue-drab" target="_blank" rel="noopener noreferrer">Telfar Saggy Sweat Jean - Blue/Drab</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://www.mrporter.com/en-ch/mens/product/greg-lauren/clothing/winter-coats/faux-fur-trimmed-quilted-cotton-blend-twill-and-denim-hooded-jacket/1647597342551314" target="_blank" rel="noopener noreferrer">Greg Lauren Faux Fur-Trimmed Quilted Cotton-Blend Twill and Denim Hooded Jacket</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://nepenthesny.com/products/fa-pant-charcoal-tropical-wool" target="_blank" rel="noopener noreferrer">Engineered Garments FA Pant - Charcoal Tropical Wool</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://acrnm.com/SP29-M_SS23" target="_blank" rel="noopener noreferrer">Acronym Nylon Stretch BDU Short Pant Gen. 1 Lightshell (WR) Gray</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://www.pinterest.com/pin/418975571605217612/" target="_blank" rel="noopener noreferrer">Photograph by Timothy Stylinski</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://www.theguardian.com/science/life-and-physics/2016/apr/13/a-deeper-understanding-of-the-universe-from-2km-underground#img-1" target="_blank" rel="noopener noreferrer">The Sudbury Neutrino Observatory</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://www.youtube.com/shorts/4GoCJepSj3A" target="_blank" rel="noopener noreferrer">Satisfying metal cube cut render by SLWondrousOfficial</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://www.pinterest.com/pin/9710955442961921/" target="_blank" rel="noopener noreferrer">Photograph</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://www.militariazone.com/trousers-shorts/original-vietnam-war-us-navy-cracker-jack-bell-bottom-trousers/itm24221" target="_blank" rel="noopener noreferrer">Original Vietnam War US Navy Cracker Jack Bell Bottom Trousers</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://www.colonellittleton.com/blog/mcclellan-cavalry-saddle/" target="_blank" rel="noopener noreferrer">The McClellan Cavalry Saddle</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://ali-f1.blogspot.com/2014/06/suspension-geometries.html" target="_blank" rel="noopener noreferrer">Vehicle Suspension</a>
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <a href="https://beautiful-people.jp/products/nanga-multilayering-down-vest-blouson" target="_blank" rel="noopener noreferrer">NANGA multilayering down vest blouson</a>
                </li>
              </ol>
            </div>
          </section>

            <h3 style={{ 
              fontSize: "22px", 
              marginBottom: "20px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Logo Ideation Board
            </h3>

            <div style={{ 
              display: "flex", 
              justifyContent: "center", 
              marginBottom: "40px",
              maxWidth: "500px",
              margin: "0 auto 40px"
            }}>
              <Suspense fallback={<LoadingPlaceholder />}>
                <ResponsiveImage
                  src={`${logoIdeationBoard}.jpg`}
                  alt="SAGA Logo Ideation Board"
                  style={{ width: "100%", height: "auto", objectFit: "contain" }}
                  sizes="(max-width: 500px) 100vw, 500px"
                  priority={false}
                />
              </Suspense>
            </div>

            <h3 style={{ 
              fontSize: "22px", 
              marginBottom: "20px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Logo Inspiration & Explanation
            </h3>

            <div style={{ 
              maxWidth: "900px",
              margin: "0 auto",
              fontSize: "16px",
              lineHeight: 1.8,
              color: "#333",
              textAlign: "left"
            }}>
              <p style={{ marginBottom: "20px" }}>
                My logo is inspired by the inextricable link between fashion and linguistics, both as a form of communication. The word Saga is typed with its phonetic transcription using both the a with an umlaut and the schwa or mid-central vowel as brand identifiers.
              </p>
              
              <p style={{ marginBottom: "20px" }}>
                Additionally, Saga was ideated using the initials of my name (<strong>SA</strong>m <strong>GA</strong>ston) to explain what my journey has been, as well as incorporating Scandinavian minimalism into my work.
              </p>
              
              <p style={{ margin: 0 }}>
                The logo you find is a visual representation of clean and refined form, aligned perfectly, and displayed through capitalization and non-capitalization to display the versatility of the brand in low tones and high tones and the persistence I have displayed through college in repetition.
              </p>
            </div>

            {/* Hang Tags */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Hang Tag
              </h3>

              <div style={{ 
                display: "flex", 
                justifyContent: "center",
                gap: "20px",
                flexWrap: "wrap",
                marginBottom: "30px",
                maxWidth: "1000px",
                margin: "0 auto 30px"
              }}>
                <div style={{ flex: "1 1 45%", minWidth: "300px" }}>
                  <Suspense fallback={<LoadingPlaceholder />}>
                    <ResponsiveImage
                      src="/images/projects/SAGA Adaptive Apparel/Hangtag_Mockup.png"
                      alt="SAGA Hang Tag Mockup"
                      style={{ width: "100%", height: "auto", objectFit: "contain" }}
                      sizes="(max-width: 500px) 100vw, 500px"
                      priority={false}
                    />
                  </Suspense>
                </div>
                <div style={{ flex: "1 1 45%", minWidth: "300px" }}>
                  <Suspense fallback={<LoadingPlaceholder />}>
                    <ResponsiveImage
                      src="/images/projects/SAGA Adaptive Apparel/AI_Hangtag_Mockup.png"
                      alt="SAGA AI Hang Tag Mockup"
                      style={{ width: "100%", height: "auto", objectFit: "contain" }}
                      sizes="(max-width: 500px) 100vw, 500px"
                      priority={false}
                    />
                  </Suspense>
                </div>
              </div>

              <div style={{ 
                maxWidth: "900px",
                margin: "0 auto",
                fontSize: "16px",
                lineHeight: 1.8,
                color: "#333",
                textAlign: "left",
                padding: "0 20px"
              }}>
                <p>
                  The hang tag establishes the brand's visual identity through minimal yet intentional luxury. A muted mauve tone serves as a conceptual placeholder, adaptable to each collection or garment. The logo extends past the tag's edge, symbolizing continuity and the relationship between garment form and wearer. This partial visibility creates recognition through fragmentation, as the consumer should be able to understand through both our products and marketing. A metal grommet reinforces the opening, and a cotton rope loop adds a tactile, soft contrast.
                </p>
              </div>
            </div>
          </CollapsibleSection>

          {/* Preliminary Research & Analysis Collapsible Section */}
          <CollapsibleSection 
            title="Preliminary Research & Analysis" 
            isOpen={researchOpen} 
            onToggle={() => setResearchOpen(!researchOpen)}
          >
          {/* Costing Section */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "30px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Costing
            </h2>

            <div style={{ 
              maxWidth: "900px",
              margin: "0 auto 30px",
              fontSize: "16px",
              lineHeight: 1.8,
              color: "#333",
              textAlign: "left"
            }}>
              <p>
                In researching existing adaptive apparel brands, I found that none priced their products above $400 USD. I intentionally positioned my pricing well below that threshold, as this industry serves a functional need rather than a luxury market. Clothing designed for accessibility should remain attainable. My pricing structure maintains a maximum margin of approximately 40 percent and a minimum of around 8 percent. This range feels appropriate for an emerging brand, reflecting both ethical considerations and the realities of startup production. Consistency across similar categories, such as outerwear, was also a priority of mine to ensure clarity and fairness in value, even in loss of margin.
              </p>
            </div>

            <div style={{ 
              display: "flex", 
              justifyContent: "center",
              marginTop: "20px"
            }}>
              <a 
                href="/pdfs/saga-adaptive-apparel/SAGA Costing.pdf"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "12px 24px",
                  backgroundColor: "#333",
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: "4px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  transition: "background-color 0.3s"
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#555"}
                onMouseOut={(e) => e.target.style.backgroundColor = "#333"}
              >
                View Costing PDF
              </a>
            </div>
          </section>

          {/* Radar Chart Analysis Section */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "30px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Radar Chart: Competitor Analysis
            </h2>

            <div style={{ 
              display: "flex", 
              justifyContent: "center", 
              marginBottom: "40px",
              maxWidth: "700px",
              margin: "0 auto 40px"
            }}>
              <Suspense fallback={<LoadingPlaceholder />}>
                <ResponsiveImage
                  src={`${radarChartImage}.jpg`}
                  alt="SAGA Adaptive Apparel Radar Chart Analysis"
                  style={{ width: "100%", height: "auto", objectFit: "contain" }}
                  sizes="(max-width: 700px) 100vw, 700px"
                  priority={true}
                />
              </Suspense>
            </div>

            <div style={{ 
              display: "flex", 
              justifyContent: "center", 
              marginBottom: "40px",
              maxWidth: "700px",
              margin: "0 auto 40px"
            }}>
              <Suspense fallback={<LoadingPlaceholder />}>
                <ResponsiveImage
                  src="/images/projects/Saga Adaptive Apparel/Gaston_Samuel_Radar_Analysis_All.jpg"
                  alt="SAGA Adaptive Apparel Full Radar Chart Analysis"
                  style={{ width: "100%", height: "auto", objectFit: "contain" }}
                  sizes="(max-width: 700px) 100vw, 700px"
                  priority={false}
                />
              </Suspense>
            </div>

            <div style={{ marginBottom: "40px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold"
              }}>
                Five Key Design Considerations
              </h3>

              <div style={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "25px" 
              }}>
                <div style={{ 
                  padding: "15px 20px", 
                  backgroundColor: "#f9f9f9", 
                  borderRadius: "6px",
                  borderLeft: "4px solid #333"
                }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "10px",
                    fontWeight: "bold"
                  }}>
                    1. Fastener Accessibility
                  </h4>
                  <p style={{ lineHeight: 1.6, margin: 0 }}>
                    This was added so that opening and closing the garment is possible and easy for the user without help.
                  </p>
                </div>

                <div style={{ 
                  padding: "15px 20px", 
                  backgroundColor: "#f9f9f9", 
                  borderRadius: "6px",
                  borderLeft: "4px solid #333"
                }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "10px",
                    fontWeight: "bold"
                  }}>
                    2. Ease of Dressing
                  </h4>
                  <p style={{ lineHeight: 1.6, margin: 0 }}>
                    This was added so the garments are easy to put on and take off day in and day out without need of assistance or minimal assistance.
                  </p>
                </div>

                <div style={{ 
                  padding: "15px 20px", 
                  backgroundColor: "#f9f9f9", 
                  borderRadius: "6px",
                  borderLeft: "4px solid #333"
                }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "10px",
                    fontWeight: "bold"
                  }}>
                    3. Fit for Seated Posture
                  </h4>
                  <p style={{ lineHeight: 1.6, margin: 0 }}>
                    This was added so that I can prioritize creating garments for people in wheelchairs.
                  </p>
                </div>

                <div style={{ 
                  padding: "15px 20px", 
                  backgroundColor: "#f9f9f9", 
                  borderRadius: "6px",
                  borderLeft: "4px solid #333"
                }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "10px",
                    fontWeight: "bold"
                  }}>
                    4. Aesthetics/Non-Stigmatizing Appearance
                  </h4>
                  <p style={{ lineHeight: 1.6, margin: 0 }}>
                    This is one of the most important things to me as I feel (and the radar chart confirms) that there is little to no representation for good looking adaptive clothing in the market.
                  </p>
                </div>

                <div style={{ 
                  padding: "15px 20px", 
                  backgroundColor: "#f9f9f9", 
                  borderRadius: "6px",
                  borderLeft: "4px solid #333"
                }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "10px",
                    fontWeight: "bold"
                  }}>
                    5. Pressure Management and Comfort
                  </h4>
                  <p style={{ lineHeight: 1.6, margin: 0 }}>
                    This is very important for seated garment wearers so they do not develop injuries from their clothing.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "15px",
                fontWeight: "bold"
              }}>
                Key Findings from Radar Analysis
              </h3>
              <p style={{ 
                lineHeight: 1.8, 
                fontSize: "16px",
                padding: "15px 20px",
                backgroundColor: "#f0f8ff",
                borderRadius: "6px"
              }}>
                Many brands meet the needs for a blanket description of a seated person, but no brand solves every issue.
              </p>
            </div>

            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "15px",
                fontWeight: "bold"
              }}>
                Market Voids Based on Chart
              </h3>
              <p style={{ 
                lineHeight: 1.8, 
                fontSize: "16px",
                padding: "15px 20px",
                backgroundColor: "#fff4e6",
                borderRadius: "6px"
              }}>
                People with disabilities are lacking accessible clothing that works well for their needs and is good looking.
              </p>
            </div>

            <div>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "15px",
                fontWeight: "bold"
              }}>
                SAGA's Market Position
              </h3>
              <p style={{ 
                lineHeight: 1.8, 
                fontSize: "16px",
                padding: "15px 20px",
                backgroundColor: "#e8f5e9",
                borderRadius: "6px"
              }}>
                I think I am building off of the experiences of disabled people and companies catering to them and mainly innovating in the style aspect.
              </p>
            </div>
          </section>

          {/* Consumer Needs & Design Solutions Table */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "30px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Consumer Needs & Design Solutions
            </h2>

            <div style={{ overflowX: "auto", marginBottom: "40px" }}>
              <table style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
                backgroundColor: "#fff",
                border: "1px solid #ddd"
              }}>
                <thead>
                  <tr style={{ backgroundColor: "#333", color: "#fff" }}>
                    <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #ddd", minWidth: "150px" }}>Consumer Need/Barrier</th>
                    <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #ddd", minWidth: "180px" }}>Impact on Dressing/Clothing Needs</th>
                    <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #ddd", minWidth: "140px" }}>Clothing Design Function</th>
                    <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #ddd", minWidth: "180px" }}>Specific Feature/Design</th>
                    <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #ddd", minWidth: "150px" }}>Preferred Fabric / Material</th>
                    <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #ddd", minWidth: "200px" }}>Application Example in Collection</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: "#f9f9f9" }}>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Pants slip down or cut into the waist when seated</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>RTW pants are designed for standing, causing discomfort or gaps at the back waist when sitting.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Fit / Protection</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Increase Garment Rise (Extend center back crotch length and reduce center front length). Elastic Band at waist for flexibility/comfort.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>N/A</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>High-rise active pants optimized for seated wear, featuring an elasticized back casing and a shortened front rise to prevent bunching.</td>
                  </tr>
                  
                  <tr>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Difficulty manipulating traditional closures</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Challenges exist with buttons, small zipper pulls, and intricate hooks due to low dexterity or mobility issues.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Simple to Use / Low Grip Strength</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Magnetic Closures (or Velcro/Snaps). Utilize protrusions like a ring attached to a zipper slider.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>N/A (Focus on closure)</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Outerwear jacket and shirts featuring magnetic closures in place of traditional buttons or zippers for one-handed operation.</td>
                  </tr>
                  
                  <tr style={{ backgroundColor: "#f9f9f9" }}>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Difficulty donning shoes over AFOs/Braces</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Orthotic devices (AFOs, braces) require shoes to be wide and deep, making mass-market shoes difficult or impossible to put on.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Open Extra-Wide / Donning/Doffing</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Zippers/Velcro that fully open the shoe (side or rear entry). Deep Heel/Wide Toe Box.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>N/A</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Athletic shoe designed with full-length side or rear zippers to open the entire shoe and accommodate orthotic devices.</td>
                  </tr>
                  
                  <tr>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Friction/abrasion ruins footwear and apparel quickly</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Mobility aids (braces, manual wheelchairs) cause high friction, tearing up shoe material and fabrics on contact points.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Durability / Protection</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Reinforce Stress Points. Modify patterns to minimize seams at pressure points.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Durable Materials (e.g., tough seams, polyester/spandex blends).</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Athletic pants constructed with tougher seams and stitching and reinforcement/padding in high-wear areas like the inner calf or side seams.</td>
                  </tr>
                  
                  <tr style={{ backgroundColor: "#f9f9f9" }}>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Discomfort caused by tags, seams, and rough textures</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Sensory issues (e.g., CRPS, tactile defensiveness, CP) mean wearers need softer fabrics, avoiding protruding elements and abrasive fabrics.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Sensory Comfort</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Flat Seams (or inside-out seams) and Tagless Options. Avoid back pockets for wheelchair users.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Soft, Stretchy/Flexible Fabrics (e.g., microfiber, fleece, performance wicking fabrics).</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Performance tops using flat seams and tagless labeling to ensure minimal irritation against sensitive skin.</td>
                  </tr>
                  
                  <tr>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Difficulty putting on jackets/sleeves when seated</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Getting arms through sleeves or adjusting a jacket is hard for seated users or those with limited range of motion; jackets often bunch up in the back.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Donning/Doffing / Easy to Reach</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Adjustable Openings (e.g., full zip/Velcro along side seams or back opening). Use a garment style like a poncho or jacket with a higher cut in the back.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Flexible/Stretchy Fabrics (Ease of movement).</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Outerwear designed with open overlapping panels in the back or a high-cut back to facilitate assisted dressing and prevent bunching when seated.</td>
                  </tr>
                  
                  <tr style={{ backgroundColor: "#f9f9f9" }}>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>RTW clothing and shoes lack style/age-appropriateness</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Users want to wear contemporary brands and look "normal," but existing adaptive wear often looks childish or institutional.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Aesthetic / Expressive Considerations (Self-esteem, value, individuality)</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Contemporary Design (Line, form, color, texture). Offer colors beyond traditional therapeutic shades.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>N/A (Focus on aesthetic qualities)</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Design line offering styles (like jeggings or streamlined athletic gear) that are fashionable and age-appropriate while incorporating necessary functional adaptations.</td>
                  </tr>
                  
                  <tr>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Need accessibility for medical devices</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Ports, G-tubes, and catheters require clothing modifications or removal for necessary access and care.</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Ability to Hold Medical Equipment</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Access Ports (e.g., bound buttonholes or small zipper openings).</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>N/A (Focus on function/feature)</td>
                    <td style={{ padding: "12px 8px", borderBottom: "1px solid #ddd", verticalAlign: "top" }}>Activewear top featuring a discrete, small zippered access point located along the neckline or abdomen for G-tube access.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Competitor Research Section */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "30px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Competitor/Inspiration Brand Analysis
            </h2>

            {/* Non-Adaptive Brands */}
            <h3 style={{ 
              fontSize: "24px", 
              marginBottom: "20px",
              marginTop: "40px",
              fontWeight: "bold"
            }}>
              Non-Adaptive Apparel Brands
            </h3>
            
            {nonAdaptiveBrands.map((brand, index) => (
              <div key={index} style={{
                marginBottom: "40px",
                padding: "20px",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px"
              }}>
                <h4 style={{ 
                  fontSize: "20px", 
                  marginBottom: "12px",
                  fontWeight: "bold"
                }}>
                  {brand.name}
                </h4>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Description:</strong> {brand.description}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Product Categories:</strong> {brand.categories}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Price Point:</strong> {brand.pricePoint} — {brand.priceRange}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Target Market:</strong> {brand.targetMarket}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Age Range:</strong> {brand.ageRange}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Location:</strong> {brand.location}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Targeted Consumers:</strong> {brand.targetedConsumers}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Competitive Edge:</strong> {brand.competitiveEdge}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Aesthetics:</strong> {brand.aesthetics}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Materials:</strong> {brand.materials}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Fit:</strong> {brand.fit}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Design Features:</strong> {brand.designFeatures}
                </div>
                
                <div>
                  <strong>Innovations:</strong> {brand.innovations}
                </div>
              </div>
            ))}

            {/* Adaptive Brands */}
            <h3 style={{ 
              fontSize: "24px", 
              marginBottom: "20px",
              marginTop: "50px",
              fontWeight: "bold"
            }}>
              Adaptive Apparel Brands
            </h3>
            
            {adaptiveBrands.map((brand, index) => (
              <div key={index} style={{
                marginBottom: "40px",
                padding: "20px",
                backgroundColor: "#f0f8ff",
                borderRadius: "8px"
              }}>
                <h4 style={{ 
                  fontSize: "20px", 
                  marginBottom: "12px",
                  fontWeight: "bold"
                }}>
                  {brand.name}
                </h4>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Description:</strong> {brand.description}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Product Categories:</strong> {brand.categories}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Price Point:</strong> {brand.pricePoint} — {brand.priceRange}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Target Market:</strong> {brand.targetMarket}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Age Range:</strong> {brand.ageRange}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Location:</strong> {brand.location}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Targeted Consumers:</strong> {brand.targetedConsumers}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Competitive Edge:</strong> {brand.competitiveEdge}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Aesthetics:</strong> {brand.aesthetics}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Materials:</strong> {brand.materials}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Fit:</strong> {brand.fit}
                </div>
                
                <div style={{ marginBottom: "10px" }}>
                  <strong>Design Features:</strong> {brand.designFeatures}
                </div>
                
                <div>
                  <strong>Innovations:</strong> {brand.innovations}
                </div>
              </div>
            ))}
          </section>

          {/* Key Findings Section */}
          <section style={{ marginBottom: "60px" }}>
            <h3 style={{ 
              fontSize: "24px", 
              marginBottom: "20px",
              fontWeight: "bold"
            }}>
              Key Findings from Competitor Brand Research
            </h3>
            
            <ul style={{ 
              lineHeight: 1.8,
              fontSize: "16px",
              marginLeft: "20px"
            }}>
              <li>
                <strong>Adaptive brands focus on seated patterning and easy fastening:</strong> Common technical features are higher back rises, magnetic closures, seamless or low-seam backs (to reduce pressure sores), and cut/length adjustments for seated posture.
              </li>
              <li>
                <strong>Aesthetic convergence toward "normal" fashion:</strong> Leading adaptive labels intentionally match mainstream silhouettes and colors so garments don't read as clinical. This helps mainstream acceptance and wearability.
              </li>
              <li>
                <strong>Price and accessibility vary:</strong> Adaptive market spans relatively accessible (~$50 basics) to contemporary/bridge (~$100–$350).
              </li>
            </ul>
          </section>

          {/* Market Voids Section */}
          <section style={{ marginBottom: "60px" }}>
            <h3 style={{ 
              fontSize: "24px", 
              marginBottom: "20px",
              fontWeight: "bold"
            }}>
              Voids in the Market
            </h3>
            
            <ul style={{ 
              lineHeight: 1.8,
              fontSize: "16px",
              marginLeft: "20px"
            }}>
              <li>
                <strong>ACRONYM level tech + Kinetic/IZ seated ergonomics doesn't exist:</strong> Few brands blend high-performance membranes/technical trims with true seated/adaptive patterning.
              </li>
              <li>
                <strong>Adaptive brands remain niche:</strong> Apart from small collaborations, major retailers and mass channels still under-serve adaptive product ranges (styling options, sizes, price points).
              </li>
              <li>
                <strong>Limited formal/occasion wear:</strong> There are tailored pieces, but selection for formal/occasion wear that convincingly matches non-adaptive eveningwear (in multiple price tiers) is limited.
              </li>
              <li>
                <strong>Lack of clinically tested innovations:</strong> Aside from patents like IZ's seamless back pant, there's limited publicized use of clinically tested fabrics (pressure-relief textiles) and formal medical/rehab partnerships.
              </li>
              <li>
                <strong>No luxury adaptive options:</strong> Competitors either sit in accessible/bridge pricing or technical performance. No luxury textiles (cashmere, fine Italian wools, silk blends) paired with adaptive engineering.
              </li>
              <li>
                <strong>Fashion-first design is missing:</strong> Adaptive labels design for usability first and aesthetic parity second at most; non-adaptive fashion brands have not integrated adaptive features as fashion-led.
              </li>
              <li>
                <strong>No mechanical integration:</strong> No examples of garments designed to mechanically link to wheelchairs/leg braces (clips, magnetic docking, modular attachment points).
              </li>
            </ul>
          </section>

          {/* Future Trends Section */}
          <section style={{ marginBottom: "60px" }}>
            <h3 style={{ 
              fontSize: "24px", 
              marginBottom: "20px",
              fontWeight: "bold"
            }}>
              Key Areas & Trends Likely to Impact Adaptive Apparel (1-2 Years)
            </h3>
            
            <ul style={{ 
              lineHeight: 1.8,
              fontSize: "16px",
              marginLeft: "20px"
            }}>
              <li>Adaptive features will increasingly appear in mainstream collections and brand collaborations</li>
              <li>More user-led design and iterative products</li>
              <li>A subscription service that guarantees outfit fit and mobility-device compatibility via tailoring with an initial fitting and annual refit sessions</li>
              <li>3D body-scan using phone and AR try-on for adaptive garments based on needs</li>
            </ul>
          </section>
          </CollapsibleSection>

          {/* Flats Collapsible Section */}
          <CollapsibleSection 
            title="Flat Sketches" 
            isOpen={flatsOpen} 
            onToggle={() => setFlatsOpen(!flatsOpen)}
          >
            {/* Garment Collection Section */}
            <section style={{ marginBottom: "60px" }}>
              <h2 style={{ 
                fontSize: "28px", 
                marginBottom: "30px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Garment Collection
              </h2>

              {/* Made-to-Measure Harrington Jacket */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Made-to-Measure Harrington Jacket
              </h3>

              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                marginBottom: "30px",
                maxWidth: "800px",
                margin: "0 auto 30px"
              }}>
                <Suspense fallback={<LoadingPlaceholder />}>
                  <ResponsiveImage
                    src="/images/projects/Saga Adaptive Apparel/Flats/MTM Harrington Black White.png"
                    alt="Made-to-Measure Harrington Jacket"
                    style={{ width: "100%", height: "auto", objectFit: "contain" }}
                    sizes="(max-width: 800px) 100vw, 800px"
                    priority={false}
                  />
                </Suspense>
              </div>

              <div style={{ 
                maxWidth: "900px",
                margin: "0 auto",
                fontSize: "16px",
                lineHeight: 1.8,
                color: "#333",
                textAlign: "left",
                padding: "0 20px"
              }}>
                <p>
                  The idea for this jacket is to put a blank bodice on my partner for the project and have them display to me their articulation through their arm and hand movements so I can base pocket shape, size, and placement directly to be tailored to them and their specific needs. This is inspired by ACRONYM's first-person design philosophy.
                </p>
              </div>
            </div>

            {/* Sweatpant Jeans */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Sweatpant Jeans
              </h3>

              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                marginBottom: "30px",
                maxWidth: "800px",
                margin: "0 auto 30px"
              }}>
                <Suspense fallback={<LoadingPlaceholder />}>
                  <ResponsiveImage
                    src="/images/projects/SAGA Adaptive Apparel/Flats/Sweat Jeans.jpg"
                    alt="Sweatpant Jeans"
                    style={{ width: "100%", height: "auto", objectFit: "contain" }}
                    sizes="(max-width: 800px) 100vw, 800px"
                    priority={false}
                  />
                </Suspense>
              </div>

              <div style={{ 
                maxWidth: "900px",
                margin: "0 auto",
                fontSize: "16px",
                lineHeight: 1.8,
                color: "#333",
                textAlign: "left",
                padding: "0 20px"
              }}>
                <p>
                  Through a more avant-garde and fashion driven approach, the sweatpant jean is a pair of denim attached to a pair of sweatpants by the waistband and curved paneling. This would benefit the comfort of the wearer if they are required to be in a wheelchair for most of the day, while also making their clothing look interesting both from the front and the back side. The sweatpants also feature the removal of a center back seam and the remaining of traditional sweatpant elasticated waistband to further facilitate comfort.
                </p>
              </div>
            </div>

            {/* Side Entry Quarter-Zip */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Side Entry Quarter-Zip
              </h3>

              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                marginBottom: "30px",
                maxWidth: "800px",
                margin: "0 auto 30px"
              }}>
                <Suspense fallback={<LoadingPlaceholder />}>
                  <ResponsiveImage
                    src="/images/projects/SAGA Adaptive Apparel/Flats/Side Entry Quarter-Zip.jpg"
                    alt="Side Entry Quarter-Zip"
                    style={{ width: "100%", height: "auto", objectFit: "contain" }}
                    sizes="(max-width: 800px) 100vw, 800px"
                    priority={false}
                  />
                </Suspense>
              </div>

              <div style={{ 
                maxWidth: "900px",
                margin: "0 auto",
                fontSize: "16px",
                lineHeight: 1.8,
                color: "#333",
                textAlign: "left",
                padding: "0 20px"
              }}>
                <p>
                  The side entry quarter zip has a fully encompassing zipper along the side body seam and sleeve seam. It allows for easy entry and a more interesting front and back appearance.
                </p>
              </div>
            </div>

            {/* Padded Back Jacket */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Padded Back Jacket
              </h3>

              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                marginBottom: "30px",
                maxWidth: "800px",
                margin: "0 auto 30px"
              }}>
                <Suspense fallback={<LoadingPlaceholder />}>
                  <ResponsiveImage
                    src="/images/projects/SAGA Adaptive Apparel/Flats/Padded Back Jacket.jpg"
                    alt="Padded Back Jacket"
                    style={{ width: "100%", height: "auto", objectFit: "contain" }}
                    sizes="(max-width: 800px) 100vw, 800px"
                    priority={false}
                  />
                </Suspense>
              </div>

              <div style={{ 
                maxWidth: "900px",
                margin: "0 auto",
                fontSize: "16px",
                lineHeight: 1.8,
                color: "#333",
                textAlign: "left",
                padding: "0 20px"
              }}>
                <p>
                  Diamond quilted back panels appliques are featured that can be filled with padding, ice packs, or hot packs to help the user remain comfortable while their back is pressed against a seat rest.
                </p>
              </div>
            </div>

            {/* Snap Inseam Shorts */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Snap Inseam Shorts
              </h3>

              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                marginBottom: "30px",
                maxWidth: "800px",
                margin: "0 auto 30px"
              }}>
                <Suspense fallback={<LoadingPlaceholder />}>
                  <ResponsiveImage
                    src="/images/projects/SAGA Adaptive Apparel/Flats/Snap Inseam Shorts.jpg"
                    alt="Snap Inseam Shorts"
                    style={{ width: "100%", height: "auto", objectFit: "contain" }}
                    sizes="(max-width: 800px) 100vw, 800px"
                    priority={false}
                  />
                </Suspense>
              </div>

              <div style={{ 
                maxWidth: "900px",
                margin: "0 auto",
                fontSize: "16px",
                lineHeight: 1.8,
                color: "#333",
                textAlign: "left",
                padding: "0 20px"
              }}>
                <p>
                  Snaps along the inseam of the short and crotch seam for easy dressing and removal.
                </p>
              </div>
            </div>
          </section>

          {/* Colored Flats Section */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "30px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Colored Flats
            </h2>

            {/* MTM Harrington Flats */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Made-to-Measure Harrington Jacket
              </h3>
              <div style={{ 
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "20px",
                maxWidth: "1000px",
                margin: "0 auto"
              }}>
                {[1, 2, 3].map((colorway) => (
                  <div key={`mtm-flat-${colorway}`}>
                    <Suspense fallback={<LoadingPlaceholder />}>
                      <ResponsiveImage
                        src={`/images/projects/Saga Adaptive Apparel/Color Up Flats/MTM Harrington Colorway ${colorway}.png`}
                        alt={`MTM Harrington Colorway ${colorway} Flat`}
                        style={{ width: "100%", height: "auto", objectFit: "contain" }}
                        sizes="(max-width: 600px) 100vw, 33vw"
                        priority={false}
                      />
                    </Suspense>
                    <a href={`/images/projects/Saga Adaptive Apparel/Color Up Flats/MTM Harrington Colorway ${colorway}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                    <p style={{ textAlign: "center", marginTop: "10px", color: "#555" }}>Colorway {colorway}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Padded Back Jacket Flats */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Padded Back Jacket
              </h3>
              <div style={{ 
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "20px",
                maxWidth: "1000px",
                margin: "0 auto"
              }}>
                {[1, 2, 3].map((colorway) => (
                  <div key={`padded-flat-${colorway}`}>
                    <Suspense fallback={<LoadingPlaceholder />}>
                      <ResponsiveImage
                        src={`/images/projects/Saga Adaptive Apparel/Color Up Flats/Padded Back Jacket Colorway ${colorway}.png`}
                        alt={`Padded Back Jacket Colorway ${colorway} Flat`}
                        style={{ width: "100%", height: "auto", objectFit: "contain" }}
                        sizes="(max-width: 600px) 100vw, 33vw"
                        priority={false}
                      />
                    </Suspense>
                    <a href={`/images/projects/Saga Adaptive Apparel/Color Up Flats/Padded Back Jacket Colorway ${colorway}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                    <p style={{ textAlign: "center", marginTop: "10px", color: "#555" }}>Colorway {colorway}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quarter Zip Flats */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Side Entry Quarter-Zip
              </h3>
              <div style={{ 
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "20px",
                maxWidth: "1000px",
                margin: "0 auto"
              }}>
                {[1, 2, 3].map((colorway) => (
                  <div key={`qz-flat-${colorway}`}>
                    <Suspense fallback={<LoadingPlaceholder />}>
                      <ResponsiveImage
                        src={`/images/projects/Saga Adaptive Apparel/Color Up Flats/Quarter Zip Colorway ${colorway}.png`}
                        alt={`Quarter Zip Colorway ${colorway} Flat`}
                        style={{ width: "100%", height: "auto", objectFit: "contain" }}
                        sizes="(max-width: 600px) 100vw, 33vw"
                        priority={false}
                      />
                    </Suspense>
                    <a href={`/images/projects/Saga Adaptive Apparel/Color Up Flats/Quarter Zip Colorway ${colorway}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                    <p style={{ textAlign: "center", marginTop: "10px", color: "#555" }}>Colorway {colorway}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Snap Inseam Short Flats */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Snap Inseam Shorts
              </h3>
              <div style={{ 
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "20px",
                maxWidth: "1000px",
                margin: "0 auto"
              }}>
                {[1, 2, 3].map((colorway) => (
                  <div key={`snap-flat-${colorway}`}>
                    <Suspense fallback={<LoadingPlaceholder />}>
                      <ResponsiveImage
                        src={`/images/projects/Saga Adaptive Apparel/Color Up Flats/Snap Inseam Short Colorway ${colorway}.png`}
                        alt={`Snap Inseam Short Colorway ${colorway} Flat`}
                        style={{ width: "100%", height: "auto", objectFit: "contain" }}
                        sizes="(max-width: 600px) 100vw, 33vw"
                        priority={false}
                      />
                    </Suspense>
                    <a href={`/images/projects/Saga Adaptive Apparel/Color Up Flats/Snap Inseam Short Colorway ${colorway}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                    <p style={{ textAlign: "center", marginTop: "10px", color: "#555" }}>Colorway {colorway}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Sweat Jeans Flats */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Sweatpant Jeans
              </h3>
              <div style={{ 
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "20px",
                maxWidth: "1000px",
                margin: "0 auto"
              }}>
                {[1, 2, 3].map((colorway) => (
                  <div key={`sweat-flat-${colorway}`}>
                    <Suspense fallback={<LoadingPlaceholder />}>
                      <ResponsiveImage
                        src={`/images/projects/Saga Adaptive Apparel/Color Up Flats/Sweat Jeans Colorway ${colorway}.png`}
                        alt={`Sweat Jeans Colorway ${colorway} Flat`}
                        style={{ width: "100%", height: "auto", objectFit: "contain" }}
                        sizes="(max-width: 600px) 100vw, 33vw"
                        priority={false}
                      />
                    </Suspense>
                    <a href={`/images/projects/Saga Adaptive Apparel/Color Up Flats/Sweat Jeans Colorway ${colorway}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                    <p style={{ textAlign: "center", marginTop: "10px", color: "#555" }}>Colorway {colorway}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          </CollapsibleSection>

          {/* Renders Collapsible Section */}
          <CollapsibleSection 
            title="Clo3D Renders" 
            isOpen={rendersOpen} 
            onToggle={() => setRendersOpen(!rendersOpen)}
          >
          {/* Color Renders Section */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "30px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Color Renders
            </h2>

            {/* MTM Harrington */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Made-to-Measure Harrington Jacket
              </h3>

              {[1, 2, 3].map((colorway) => (
                <div key={`mtm-harrington-${colorway}`} style={{ marginBottom: "40px" }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "15px",
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#555"
                  }}>
                    Colorway {colorway}
                  </h4>
                  <div style={{ 
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "20px",
                    maxWidth: "1000px",
                    margin: "0 auto"
                  }}>
                    {["Front", "Back", "Three Quarter"].map((view) => (
                      <div key={`mtm-harrington-${colorway}-${view}`}>
                        <Suspense fallback={<LoadingPlaceholder />}>
                          <ResponsiveImage
                            src={`/images/projects/Saga Adaptive Apparel/Renders/MTM Harrington Colorway ${colorway} ${view}.png`}
                            alt={`MTM Harrington Colorway ${colorway} ${view}`}
                            style={{ width: "100%", height: "auto", objectFit: "contain" }}
                            sizes="(max-width: 600px) 100vw, 33vw"
                            priority={false}
                          />
                        </Suspense>
                        <a href={`/images/projects/Saga Adaptive Apparel/Renders/MTM Harrington Colorway ${colorway} ${view}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Padded Back Jacket */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Padded Back Jacket
              </h3>

              {[1, 2, 3].map((colorway) => (
                <div key={`padded-back-${colorway}`} style={{ marginBottom: "40px" }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "15px",
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#555"
                  }}>
                    Colorway {colorway}
                  </h4>
                  <div style={{ 
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "20px",
                    maxWidth: "1000px",
                    margin: "0 auto"
                  }}>
                    {["Front", "Back", "Three Quarter"].map((view) => (
                      <div key={`padded-back-${colorway}-${view}`}>
                        <Suspense fallback={<LoadingPlaceholder />}>
                          <ResponsiveImage
                            src={`/images/projects/Saga Adaptive Apparel/Renders/Padded Back Jacket Colorway ${colorway} ${view}.png`}
                            alt={`Padded Back Jacket Colorway ${colorway} ${view}`}
                            style={{ width: "100%", height: "auto", objectFit: "contain" }}
                            sizes="(max-width: 600px) 100vw, 33vw"
                            priority={false}
                          />
                        </Suspense>
                        <a href={`/images/projects/Saga Adaptive Apparel/Renders/Padded Back Jacket Colorway ${colorway} ${view}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Quarter Zip */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Side Entry Quarter-Zip
              </h3>

              {[1, 2, 3].map((colorway) => (
                <div key={`quarter-zip-${colorway}`} style={{ marginBottom: "40px" }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "15px",
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#555"
                  }}>
                    Colorway {colorway}
                  </h4>
                  <div style={{ 
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "20px",
                    maxWidth: "1000px",
                    margin: "0 auto"
                  }}>
                    {["Front", "Back", "Three Quarter", "Zip Three Quarter"].map((view) => (
                      <div key={`quarter-zip-${colorway}-${view}`}>
                        <Suspense fallback={<LoadingPlaceholder />}>
                          <ResponsiveImage
                            src={`/images/projects/Saga Adaptive Apparel/Renders/Quarter Zip Colorway ${colorway} ${view}.png`}
                            alt={`Quarter Zip Colorway ${colorway} ${view}`}
                            style={{ width: "100%", height: "auto", objectFit: "contain" }}
                            sizes="(max-width: 600px) 100vw, 25vw"
                            priority={false}
                          />
                        </Suspense>
                        <a href={`/images/projects/Saga Adaptive Apparel/Renders/Quarter Zip Colorway ${colorway} ${view}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Snap Inseam Shorts */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Snap Inseam Shorts
              </h3>

              {[1, 2, 3].map((colorway) => (
                <div key={`snap-shorts-${colorway}`} style={{ marginBottom: "40px" }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "15px",
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#555"
                  }}>
                    Colorway {colorway}
                  </h4>
                  <div style={{ 
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "20px",
                    maxWidth: "1000px",
                    margin: "0 auto"
                  }}>
                    {["Front", "Back", "Three Quarter"].map((view) => (
                      <div key={`snap-shorts-${colorway}-${view}`}>
                        <Suspense fallback={<LoadingPlaceholder />}>
                          <ResponsiveImage
                            src={`/images/projects/Saga Adaptive Apparel/Renders/Snap Inseam Short Colorway ${colorway} ${view}.png`}
                            alt={`Snap Inseam Short Colorway ${colorway} ${view}`}
                            style={{ width: "100%", height: "auto", objectFit: "contain" }}
                            sizes="(max-width: 600px) 100vw, 33vw"
                            priority={false}
                          />
                        </Suspense>
                        <a href={`/images/projects/Saga Adaptive Apparel/Renders/Snap Inseam Short Colorway ${colorway} ${view}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Sweat Jeans */}
            <div style={{ marginBottom: "50px" }}>
              <h3 style={{ 
                fontSize: "22px", 
                marginBottom: "20px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Sweatpant Jeans
              </h3>

              {[1, 2, 3].map((colorway) => (
                <div key={`sweat-jeans-${colorway}`} style={{ marginBottom: "40px" }}>
                  <h4 style={{ 
                    fontSize: "18px", 
                    marginBottom: "15px",
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#555"
                  }}>
                    Colorway {colorway}
                  </h4>
                  <div style={{ 
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "20px",
                    maxWidth: "1000px",
                    margin: "0 auto"
                  }}>
                    {["Front", "Back", "Three Quarter"].map((view) => (
                      <div key={`sweat-jeans-${colorway}-${view}`}>
                        <Suspense fallback={<LoadingPlaceholder />}>
                          <ResponsiveImage
                            src={`/images/projects/Saga Adaptive Apparel/Renders/Sweat Jeans Colorway ${colorway} ${view}.png`}
                            alt={`Sweat Jeans Colorway ${colorway} ${view}`}
                            style={{ width: "100%", height: "auto", objectFit: "contain" }}
                            sizes="(max-width: 600px) 100vw, 33vw"
                            priority={false}
                          />
                        </Suspense>
                        <a href={`/images/projects/Saga Adaptive Apparel/Renders/Sweat Jeans Colorway ${colorway} ${view}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
          </CollapsibleSection>

          {/* Real Images Collapsible Section */}
          <CollapsibleSection 
            title="Sewn Prototype" 
            isOpen={realImagesOpen} 
            onToggle={() => setRealImagesOpen(!realImagesOpen)}
          >
          {/* Process Images Section */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "30px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Process Images
            </h2>

            <div style={{ 
              display: "flex",
              flexDirection: "column",
              gap: "30px",
              maxWidth: "800px",
              margin: "0 auto"
            }}>
              {[
                { name: "Final Process 1", ext: "jpg" },
                { name: "Final Process 2", ext: "jpg" }
              ].map((img) => (
                <div key={img.name}>
                  <Suspense fallback={<LoadingPlaceholder />}>
                    <ResponsiveImage
                      src={`/images/projects/Saga Adaptive Apparel/${img.name}.${img.ext}`}
                      alt={img.name}
                      style={{ width: "100%", height: "auto", objectFit: "contain" }}
                      sizes="(max-width: 800px) 100vw, 800px"
                      priority={false}
                    />
                  </Suspense>
                  <a href={`/images/projects/Saga Adaptive Apparel/${img.name}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                </div>
              ))}
            </div>
          </section>

          {/* Final Garment Photos Section */}
          <section style={{ marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "28px", 
              marginBottom: "30px",
              fontWeight: "bold",
              textAlign: "center"
            }}>
              Final Garment Photos
            </h2>

            {/* All Photos - Single Column */}
            <div style={{ 
              display: "flex",
              flexDirection: "column",
              gap: "30px",
              maxWidth: "800px",
              margin: "0 auto"
            }}>
              {[
                { name: "James Front View", ext: "jpg" },
                { name: "James Far Front", ext: "jpg" },
                { name: "James Far Three Quarter", ext: "jpg" },
                { name: "James Near Side", ext: "jpg" },
                { name: "Sam and James", ext: "jpg" },
                { name: "James Interior Pocket Detail", ext: "jpg" },
                { name: "Collar Up Detail", ext: "JPG" },
                { name: "Cuff Detail", ext: "JPG" },
                { name: "Three Quarter Detail", ext: "JPG" },
                { name: "Low Angle Medium Detail", ext: "JPG" },
                { name: "Top Front Flat", ext: "JPG" },
                { name: "Flat Front Collar Up", ext: "JPG" },
                { name: "Angle Flat With Pocket", ext: "JPG" },
                { name: "Back View Flat", ext: "JPG" },
                { name: "Inside Flat", ext: "JPG" }
              ].map((img) => (
                <div key={img.name}>
                  <Suspense fallback={<LoadingPlaceholder />}>
                    <ResponsiveImage
                      src={`/images/projects/Saga Adaptive Apparel/Final Garment Photos/${img.name}.${img.ext}`}
                      alt={img.name}
                      style={{ width: "100%", height: "auto", objectFit: "contain" }}
                      sizes="(max-width: 800px) 100vw, 800px"
                      priority={false}
                    />
                  </Suspense>
                  <a href={`/images/projects/Saga Adaptive Apparel/Final Garment Photos/${img.name}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                </div>
              ))}
            </div>
          </section>
          </CollapsibleSection>

          {/* Final Results Collapsible Section */}
          <CollapsibleSection 
            title="Final Results" 
            isOpen={finalResultsOpen} 
            onToggle={() => setFinalResultsOpen(!finalResultsOpen)}
          >
            <section style={{ marginBottom: "60px" }}>
              <div style={{ 
                display: "flex",
                flexDirection: "column",
                gap: "50px",
                maxWidth: "1000px",
                margin: "0 auto"
              }}>
                {/* Presentation Slide */}
                <div>
                  <h3 style={{ 
                    fontSize: "22px", 
                    marginBottom: "20px",
                    fontWeight: "600",
                    textAlign: "center"
                  }}>
                    Presentation Slide
                  </h3>
                  <Suspense fallback={<LoadingPlaceholder />}>
                    <ResponsiveImage
                      src="/images/projects/Saga Adaptive Apparel/Presentation Slide.jpg"
                      alt="Presentation Slide"
                      style={{ width: "100%", height: "auto", objectFit: "contain" }}
                      sizes="(max-width: 1000px) 100vw, 1000px"
                      priority={false}
                    />
                  </Suspense>
                  <a href="/images/projects/Saga Adaptive Apparel/Presentation Slide.webp" target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                </div>

                {/* Poster */}
                <div>
                  <h3 style={{ 
                    fontSize: "22px", 
                    marginBottom: "20px",
                    fontWeight: "600",
                    textAlign: "center"
                  }}>
                    Poster
                  </h3>
                  <Suspense fallback={<LoadingPlaceholder />}>
                    <ResponsiveImage
                      src="/images/projects/Saga Adaptive Apparel/Poster.jpg"
                      alt="Poster"
                      style={{ width: "100%", height: "auto", objectFit: "contain" }}
                      sizes="(max-width: 1000px) 100vw, 1000px"
                      priority={false}
                    />
                  </Suspense>
                  <a href="/images/projects/Saga Adaptive Apparel/Poster.webp" target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn" style={{ display: "block", textAlign: "right", marginTop: "5px" }}>Open in new tab</a>
                </div>

                {/* Award Photo */}
                <div>
                  <h3 style={{ 
                    fontSize: "22px", 
                    marginBottom: "20px",
                    fontWeight: "600",
                    textAlign: "center"
                  }}>
                    Award Photo - 1st Place Winner Awarded by Industry Judges
                  </h3>
                  <Suspense fallback={<LoadingPlaceholder />}>
                    <ResponsiveImage
                      src="/images/projects/Saga Adaptive Apparel/Award Photo.JPG"
                      alt="Award Photo"
                      style={{ width: "100%", height: "auto", objectFit: "contain" }}
                      sizes="(max-width: 1000px) 100vw, 1000px"
                      priority={false}
                    />
                  </Suspense>
                </div>
              </div>
            </section>
          </CollapsibleSection>

          {/* Image Gallery */}
          {images.length > 0 && (
            <section style={{ marginTop: "60px" }}>
              <h3 style={{ 
                fontSize: "24px", 
                marginBottom: "30px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                Project Gallery
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "40px", marginBottom: "50px" }}>
                {images.map((base, i) => (
                  <div key={`saga-image-${i}`}>
                    <div style={{ display: "flex", justifyContent: "center", margin: "0 auto", maxWidth: "1000px" }}>
                      <Suspense fallback={<LoadingPlaceholder />}>
                        <ResponsiveImage
                          src={`${base}.webp`}
                          alt={`SAGA Adaptive Apparel image ${i + 1}`}
                          style={{ width: "100%", height: "auto", objectFit: "contain" }}
                          sizes="(max-width: 1000px) 100vw, 1000px"
                          loading={i < 3 ? "eager" : "lazy"}
                          priority={i < 1}
                        />
                      </Suspense>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", maxWidth: "1000px", margin: "0 auto" }}>
                      <a href={`${base}.webp`} target="_blank" rel="noopener noreferrer" className="open-in-new-tab-btn">Open in new tab</a>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

        </div>
      </main>

      <Footer />
    </div>
  )
}

export default SagaAdaptiveApparel
